import { defineStore } from 'pinia';
import http from '@/services/http';
import cacheService from '@/services/cacheService';
import { API_PATHS } from '@/config/api';

// Der Pinia-Store für Bewerber
export const useApplicantsStore = defineStore('applicants', {
  state: () => ({
    applicants: [],
    currentApplicant: null,
    loading: false,
    error: null,
    initialized: false
  }),
  
  getters: {
    getAllApplicants: (state) => state.applicants,
    getCurrentApplicant: (state) => state.currentApplicant,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    isInitialized: (state) => state.initialized
  },
  
  actions: {
    setApplicants(applicants) {
      console.log('Pinia setApplicants mit Daten:', applicants);
      // Stelle sicher, dass immer ein Array gesetzt wird
      this.applicants = Array.isArray(applicants) ? applicants : [];
    },
    
    setCurrentApplicant(applicant) {
      this.currentApplicant = applicant;
    },
    
    setLoading(status) {
      this.loading = status;
    },
    
    setError(error) {
      this.error = error;
    },
    
    // Initialisierung des Stores
    initialize() {
      if (!this.initialized) {
        console.log('Initialisiere Bewerber-Store...');
        // Hier können wir Cachewerte oder andere Initialisierungsaktionen durchführen
        this.initialized = true;
        return this.fetchApplicants();
      }
      return Promise.resolve(this.applicants);
    },
    
    // Asynchrone Aktionen
    async fetchApplicants() {
      this.setLoading(true);
      this.setError(null);
      
      // Detailliertes Logging für Debugging-Zwecke
      console.log('Bewerber-Abruf gestartet...');
      
      try {
        // Führe zuerst eine einfache API-Anfrage aus, um den Authentifizierungsstatus zu prüfen
        
        // Jetzt die eigentlichen Bewerber abrufen
        console.log('Rufe Bewerber-API auf...');
        const response = await http.get('/api/applicants');
        console.log('Bewerber-API antwort erhalten:', response);
        
        // Verarbeite verschiedene mögliche Antwortformate
        let applicantsData = [];
        
        if (response.data && response.data.success && response.data.applicants) {
          // Format: {success: true, applicants: {...}}
          const applicantsObj = response.data.applicants;
          
          if (applicantsObj.data && Array.isArray(applicantsObj.data)) {
            // Paginiertes Format: {current_page: 1, data: [...], ...}
            applicantsData = applicantsObj.data;
            console.log(`${applicantsData.length} Bewerber aus paginiertem Ergebnis geladen.`);
          } else if (Array.isArray(applicantsObj)) {
            // Direktes Array in applicants
            applicantsData = applicantsObj;
            console.log(`${applicantsData.length} Bewerber aus applicants-Array geladen.`);
          } else if (typeof applicantsObj === 'object') {
            // Falls applicants ein Objekt ist, aber kein Paginierungsobjekt
            applicantsData = [applicantsObj]; // Einzelner Bewerber als Array
            console.log('Einzelner Bewerber als Objekt erhalten, in Array konvertiert.');
          }
        } else if (response.data && Array.isArray(response.data)) {
          // Direktes Array in der Antwort
          applicantsData = response.data;
          console.log(`${applicantsData.length} Bewerber aus direktem Array geladen.`);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Paginiertes Format direkt in der Antwort
          applicantsData = response.data.data;
          console.log(`${applicantsData.length} Bewerber aus direktem paginierten Ergebnis geladen.`);
        } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Einzelner Bewerber als Objekt
          const possibleApplicant = response.data;
          if (possibleApplicant.id) {
            applicantsData = [possibleApplicant];
            console.log('Einzelner Bewerber als direktes Objekt erhalten.');
          }
        }
        
        console.log('Finale Bewerberdaten für den Store:', applicantsData);
        this.setApplicants(applicantsData);
        this.setLoading(false);
        return applicantsData;
      } catch (error) {
        console.error('Fehler beim Abrufen der Bewerber:', error);
        console.error('Fehlerdetails:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        
        if (error.response?.status === 401) {
          this.setError('Sie sind nicht angemeldet oder Ihre Sitzung ist abgelaufen. Bitte melden Sie sich an.');
        } else if (error.response?.status === 403) {
          this.setError('Sie haben keine Berechtigung, auf diese Daten zuzugreifen.');
        } else {
          this.setError('Fehler beim Laden der Bewerber. Bitte versuchen Sie es später erneut.');
        }
        
        this.setApplicants([]);
        this.setLoading(false);
        return [];
      }
    },
    
    async fetchApplicant(id) {
      this.setLoading(true);
      this.setError(null);
      
      // Cache-Key für einen einzelnen Bewerber
      const cacheKey = cacheService.createKey('applicants', id);
      
      try {
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log(`Bewerber ${id} aus dem Cache geladen:`, cachedData);
          this.setCurrentApplicant(cachedData);
          this.setLoading(false);
          return cachedData;
        }
        
        console.log(`Bewerber ${id} wird vom Backend abgerufen...`);
        const response = await http.get(`/api/applicants/${id}`);
        console.log(`Backend-Antwort für Bewerber ${id}:`, response);
        
        let applicantData = response.data;
        if (response.data && response.data.applicant) {
          applicantData = response.data.applicant;
        }
        
        // Normalisiere die Daten
        const normalizedApplicant = {
          id: applicantData.id || 0,
          first_name: applicantData.first_name || '',
          last_name: applicantData.last_name || '',
          email: applicantData.email || '',
          phone: applicantData.phone || '',
          status: applicantData.status || 'new',
          created_at: applicantData.created_at || new Date().toISOString(),
          test_count: applicantData.test_count || 0,
          ...applicantData
        };
        
        this.setCurrentApplicant(normalizedApplicant);
        
        // Speichere die Daten im Cache
        cacheService.set(cacheKey, normalizedApplicant);
        
        return normalizedApplicant;
      } catch (error) {
        console.error(`Fehler beim Abrufen des Bewerbers ${id}:`, error);
        this.setError('Bewerber nicht gefunden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async createApplicant(applicantData) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log('Neuer Bewerber wird erstellt mit Daten:', applicantData);
        const response = await http.post('/api/applicants', applicantData);
        console.log('Backend-Antwort für die Erstellung des Bewerbers:', response);
        
        // Cache für Bewerber invalidieren
        cacheService.invalidateEntityType('applicants');
        
        this.applicants.push(response.data);
        return response.data;
      } catch (error) {
        console.error('Fehler beim Erstellen des Bewerbers:', error);
        this.setError('Fehler beim Erstellen des Bewerbers');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async updateApplicant(id, applicantData) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Bewerber ${id} wird aktualisiert mit Daten:`, applicantData);
        const response = await http.put(`/api/applicants/${id}`, applicantData);
        console.log(`Backend-Antwort für die Aktualisierung des Bewerbers ${id}:`, response);
        
        // Cache für Bewerber invalidieren
        cacheService.invalidateEntityType('applicants');
        
        const index = this.applicants.findIndex(a => a.id === id);
        if (index !== -1) {
          this.applicants[index] = response.data;
        }
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Aktualisieren des Bewerbers ${id}:`, error);
        this.setError('Fehler beim Aktualisieren des Bewerbers');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async deleteApplicant(id) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Bewerber ${id} wird gelöscht...`);
        await http.delete(`/api/applicants/${id}`);
        console.log(`Bewerber ${id} erfolgreich gelöscht`);
        
        // Cache für Bewerber invalidieren
        cacheService.invalidateEntityType('applicants');
        
        this.applicants = this.applicants.filter(a => a.id !== id);
        return true;
      } catch (error) {
        console.error(`Fehler beim Löschen des Bewerbers ${id}:`, error);
        this.setError('Fehler beim Löschen des Bewerbers');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    // Manuelle Invalidierung des Caches
    invalidateCache() {
      cacheService.invalidateEntityType('applicants');
      console.log('Bewerber-Cache wurde manuell invalidiert');
    },
    
    // Test einem Bewerber zuweisen
    async assignTest({ applicantId, testId }) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Test ${testId} wird dem Bewerber ${applicantId} zugewiesen...`);
        const response = await http.post(`/api/applicants/${applicantId}/assign-test`, { test_id: testId });
        console.log('Zuweisung erfolgreich:', response.data);
        
        // Cache für diesen Bewerber invalidieren
        cacheService.invalidateEntity('applicants', applicantId);
        
        return response.data;
      } catch (error) {
        console.error('Fehler bei der Test-Zuweisung:', error);
        this.setError('Der Test konnte nicht zugewiesen werden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    // Test-Zuweisung aufheben
    async unassignTest({ applicantId, testId }) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Test-Zuweisung ${testId} wird für Bewerber ${applicantId} aufgehoben...`);
        const response = await http.delete(`/api/applicants/${applicantId}/tests/${testId}`);
        console.log('Zuweisung erfolgreich aufgehoben:', response.data);
        
        // Cache für diesen Bewerber invalidieren
        cacheService.invalidateEntity('applicants', applicantId);
        
        return response.data;
      } catch (error) {
        console.error('Fehler beim Aufheben der Test-Zuweisung:', error);
        this.setError('Die Test-Zuweisung konnte nicht aufgehoben werden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    }
  }
}); 